<script lang="ts" generic="T extends Object" setup>
import { useEventBus } from "@vueuse/core";

class FormException extends Error {
  constructor(message: string) {
    super(message);
    this.message = message;
    Object.setPrototypeOf(this, FormException.prototype);
  }
}

const emit = defineEmits<{
  (event: "submit", state: T): void;
  (event: "error", errors: IFormError[]): void;
}>();

const props = withDefaults(
  defineProps<{
    state: T;
    validate?: ((state: T) => Promise<IFormError[]>) | ((state: T) => IFormError[]);
    validateOn?: TFormEventType[];
  }>(),
  {
    validate: () => [],
    validateOn: () => ["blur", "input", "change", "submit"],
  },
);
const bus = useEventBus<IFormEvent>("form-bus");

bus.on(async (event) => {
  if (event.type !== "submit" && props.validateOn?.includes(event.type)) {
    await validateForm(event.path, { silent: true });
  }
});

const errors = ref<IFormError[]>([]);

provide("form-errors", errors);
provide("form-events", bus);

async function getErrors(): Promise<IFormError[]> {
  const errs = await props.validate(props.state);

  return errs;
}

async function validateForm(path?: string, opts: { silent?: boolean } = { silent: false }): Promise<T> {
  if (path) {
    const otherErrors = errors.value.filter(error => error.path !== path);
    const pathErrors = (await getErrors()).filter(error => error.path === path);
    errors.value = otherErrors.concat(pathErrors);
  }
  else {
    errors.value = await getErrors();
  }

  if (!opts.silent && errors.value.length > 0) {
    throw new FormException(`Form validation failed: ${JSON.stringify(errors.value, null, 2)}`);
  }
  return props.state;
}

async function onSubmit(): Promise<void> {
  try {
    if (props.validateOn?.includes("submit"))
      await validateForm();

    emit("submit", props.state);
  }
  catch (error) {
    if (!(error instanceof FormException)) {
      throw error;
    }

    emit("error", errors.value);
  }
}

defineExpose({
  /**
   * Triggers form validation. Will raise any errors unless `opts.silent` is set to true.
   */
  validate: validateForm,
  /**
   * A reference to the array containing validation errors. Use this to access or manipulate the error information.
   */
  errors,
  /**
   * Sets form errors for a given path. If no path is provided, overrides all errors.
   */
  setErrors(errs: IFormError[], path?: string) {
    errors.value = errs;
    if (path) {
      errors.value = errors.value.filter(error => error.path !== path).concat(errs);
    }
    else {
      errors.value = errs;
    }
  },
  /**
   * Retrieves form errors associated with a specific path. If no path is provided, returns all form errors.
   */
  getErrors(path?: string) {
    if (path)
      return errors.value.filter(err => err.path === path);

    return errors.value;
  },
  /**
   * Clears form errors associated with a specific path. If no path is provided, clears all form errors.
   */
  clear(path?: string) {
    if (path) {
      errors.value = errors.value.filter(err => err.path === path);
    }
    else {
      errors.value = [];
    }
  },
} as IForm<T>);
</script>

<template>
  <form @submit.prevent="onSubmit">
    <slot />
  </form>
</template>
